import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DateWithDataPicker
 *
 * Drop-in replacement for the standard date picker on LVM and Coupon pages.
 * Highlights dates that have saved data (green fill on the day cell) and
 * shows a "Recent entries" pill that surfaces the last 5 saved dates as
 * clickable chips. Refetches the date list when the user pages through
 * months in the calendar so the dots stay accurate.
 *
 * Props:
 *  - endpoint:   API path like "/lvm/dates/range" or "/coupons/dates/range"
 *  - selectedDate: Date — the currently selected day
 *  - onSelect:  (Date) => void — fired when user picks a date
 *  - testId:    prefix for data-testid attributes (e.g. "lvm-date")
 *  - label:     optional override for the trigger label format (default "MMM d, yyyy")
 *  - refreshKey: change to force a refetch (e.g. on save), optional
 */
export function DateWithDataPicker({
  endpoint,
  selectedDate,
  onSelect,
  testId = "data-date",
  label,
  refreshKey,
}) {
  const [open, setOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  // The month the calendar is currently displaying — drives which range we fetch.
  const [viewMonth, setViewMonth] = useState(selectedDate || new Date());
  const [dates, setDates] = useState([]); // ISO YYYY-MM-DD strings within range
  const [recent, setRecent] = useState([]); // [{date, updated_at}]

  // Sync viewMonth with the externally-selected date whenever it lands in a different month.
  useEffect(() => {
    if (!selectedDate) return;
    setViewMonth((m) => {
      if (m.getFullYear() === selectedDate.getFullYear() && m.getMonth() === selectedDate.getMonth()) {
        return m;
      }
      return selectedDate;
    });
  }, [selectedDate]);

  // Fetch data within a sensible window around the visible month.
  // We fetch the visible month ±1 so quick paging doesn't show stale dots.
  const fetchRange = useCallback(async () => {
    const start = format(startOfMonth(subMonths(viewMonth, 1)), "yyyy-MM-dd");
    const end = format(endOfMonth(addMonths(viewMonth, 1)), "yyyy-MM-dd");
    try {
      const res = await axios.get(`${API}${endpoint}`, { params: { start, end } });
      // Defensive: some endpoints used to return objects; normalize to strings.
      const rawDates = Array.isArray(res.data?.dates) ? res.data.dates : [];
      const normalized = rawDates
        .map((d) => (typeof d === "string" ? d : (d && d.date) || null))
        .filter((d) => typeof d === "string");
      setDates(normalized);
      const rawRecent = Array.isArray(res.data?.recent) ? res.data.recent : [];
      const normalizedRecent = rawRecent
        .map((d) => (typeof d === "string" ? d : (d && d.date) || null))
        .filter((d) => typeof d === "string");
      setRecent(normalizedRecent);
    } catch (_) {
      setDates([]);
      setRecent([]);
    }
  }, [endpoint, viewMonth]);

  useEffect(() => { fetchRange(); }, [fetchRange, refreshKey]);

  // Convert ISO strings to Date objects for react-day-picker modifiers.
  const dateModifiers = useMemo(() => {
    const list = dates
      .map((d) => {
        try { return parseISO(d); } catch { return null; }
      })
      .filter(Boolean);
    return { hasData: list };
  }, [dates]);

  const monthInView = useMemo(() => format(viewMonth, "yyyy-MM"), [viewMonth]);
  const entriesThisMonth = useMemo(
    () => dates.filter((d) => d.startsWith(monthInView)).length,
    [dates, monthInView]
  );

  const triggerLabel = label || format(selectedDate || new Date(), "MMM d, yyyy");

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="btn-3d w-[160px] font-mono text-sm"
            data-testid={`${testId}-picker`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {triggerLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            month={viewMonth}
            onMonthChange={setViewMonth}
            onSelect={(d) => {
              if (d) { onSelect(d); setOpen(false); }
            }}
            modifiers={dateModifiers}
            modifiersClassNames={{
              hasData:
                "bg-emerald-600/70 text-white font-bold hover:bg-emerald-600/90 aria-selected:bg-primary aria-selected:text-primary-foreground",
            }}
          />
          <div
            className="border-t px-3 py-2 text-[11px] font-mono text-muted-foreground flex items-center justify-between"
            data-testid={`${testId}-month-summary`}
          >
            <span>
              {entriesThisMonth === 0
                ? "No entries this month"
                : `${entriesThisMonth} ${entriesThisMonth === 1 ? "entry" : "entries"} this month`}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-600/70" />
              has data
            </span>
          </div>
        </PopoverContent>
      </Popover>

      {/* Recent entries mini-pill */}
      <Popover open={recentOpen} onOpenChange={setRecentOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="btn-3d"
            title={recent.length ? `${recent.length} recent entries` : "No recent entries"}
            data-testid={`${testId}-recent-btn`}
          >
            <Clock className="w-3.5 h-3.5 mr-1" />
            <span className="text-xs">Recent {recent.length > 0 && `(${recent.length})`}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          {recent.length === 0 ? (
            <div className="text-xs font-mono text-muted-foreground py-2 px-1" data-testid={`${testId}-recent-empty`}>
              No saved entries yet in this window.
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1 mb-1">
                Last {recent.length} {recent.length === 1 ? "entry" : "entries"}
              </div>
              {recent.map((r) => {
                // `recent` is normalized to ISO date strings (see fetchRange).
                // Guard parseISO so a bad value can never unmount the popover.
                let parsed = null;
                try {
                  const d = parseISO(r);
                  if (!isNaN(d.getTime())) parsed = d;
                } catch (_) { /* ignore */ }
                if (!parsed) return null;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      onSelect(parsed);
                      setRecentOpen(false);
                    }}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-secondary/60 transition-colors font-mono text-xs flex items-center justify-between"
                    data-testid={`${testId}-recent-row-${r}`}
                  >
                    <span className="font-bold">{format(parsed, "MMM d, yyyy")}</span>
                    <span className="text-muted-foreground text-[10px]">{format(parsed, "EEE")}</span>
                  </button>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
